import { useContext, useMemo, type ReactNode } from "react";
import { css } from "@emotion/react";
import { Box, useTheme } from "@mui/material";
import { Link } from "react-router-dom";

import { dayjs } from "@thalamos/common";
import {
  isAdminUser,
  isGuestUserSession,
  isSection12Accreditation,
} from "@thalamos/aspire-common";

import { LoggedInUserContext } from "../../../Contexts.js";
import { useScreenDetection } from "../../../hooks/ScreenDetection/useScreenDetection.js";
import AlertBar from "../AlertBar.js";
import DrawerDesktopMenu from "../menu/Menu.js";
import MobileMenu from "../menu/mobile/Menu.js";

export interface LayoutProps {
  children: ReactNode;
}

export const Layout = ({ children }: LayoutProps) => {
  const userContext = useContext(LoggedInUserContext);
  const user = userContext?.user!;
  const { isMobileView } = useScreenDetection();
  const theme = useTheme();

  const menuComponent = useMemo(() => {
    return isMobileView ? (
      <MobileMenu user={user} direction={"bottom"} />
    ) : (
      <DrawerDesktopMenu user={user} />
    );
  }, [isMobileView, user]);

  return (
    <>
      <Box
        css={css`
          display: flex;
          width: 100%;
          ${isMobileView && "flex-direction: column;"}
        `}
      >
        {isGuestUserSession(user) && (
          <AlertBar
            backgroundColor={theme.palette.background.lightYellow}
            color={theme.palette.common.black}
          >
            You&apos;re using Thalamos as a Guest. To access all features,
            please{" "}
            <a
              href={"https://www.thalamos.co.uk/request-demo/"}
              target={"_blank"}
              rel="noreferrer"
            >
              book a demo
            </a>{" "}
            with our team
          </AlertBar>
        )}
        {user &&
          user.sessionContext &&
          !user.address.address &&
          !isGuestUserSession(user) &&
          !isAdminUser(user) && (
            <AlertBar
              backgroundColor={theme.palette.error.main}
              color={theme.palette.common.white}
            >
              Your profile is not updated. This may prevent you from completing
              forms.{" "}
              <Link
                to={`/profile/${user.id}`}
                style={{
                  color: "white",
                  fontWeight: "bold",
                }}
              >
                Go to profile here.
              </Link>
            </AlertBar>
          )}
        {(() => {
          const s12Accreditation = user?.accreditations?.find(
            isSection12Accreditation,
          );
          const isS12Expired =
            s12Accreditation &&
            dayjs(s12Accreditation.expiryDate).isBefore(dayjs(), "day");

          return (
            isS12Expired &&
            !isGuestUserSession(user) &&
            !isAdminUser(user) && (
              <AlertBar
                backgroundColor={theme.palette.error.main}
                color={theme.palette.common.white}
              >
                Your Section 12 accreditation has expired in eMHA, please update
                in your profile as soon as possible. If you no longer intend to
                do Section 12 work, please remove the accreditation from your
                profile.{" "}
                <Link
                  to={`/profile/${user.id}`}
                  style={{
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  Go to profile here.
                </Link>
              </AlertBar>
            )
          );
        })()}
        {menuComponent}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            px: { xs: 2, sm: 3, md: 4, lg: 5 },
            ...(isMobileView ? { pt: 2 } : null),
            minHeight: "100vh",
          }}
        >
          {children}
        </Box>
      </Box>
    </>
  );
};
